/**
 * Executive Summary tab — Operational Improvement Business Case
 * + Executive Decision Report.
 *
 * Boardroom-grade layout that aggregates live consultant data:
 *
 *   1. Executive Header (engagement context strip)
 *   2. KPI Strip (Investment / Annual Savings / Payback / 3-Yr ROI /
 *                 Peak Resources / Total Duration)
 *   3. Current Operational Challenges (grouped findings)
 *   4. Executive Improvement Narrative (3-4 paragraphs)
 *   5. Recommendation Portfolio Summary (table)
 *   6. Top Value Drivers (% bars)
 *   7. Decision Statement (verdict + asks)
 *   8. Appendix — Evidence Chain (traceability per insight)
 *
 * Source: GET /api/smartimprove/engagements/{id}/business-case
 */
import { useEffect, useState, useCallback } from "react";
import {
  TrendingUp,
  Workflow,
  Layers,
  Target,
  AlertTriangle,
  Lightbulb,
  DollarSign,
  Users,
  Clock,
  CalendarRange,
  Briefcase,
  Award,
  ChevronRight,
  Building2,
  Globe2,
  Gauge,
  ScrollText,
} from "lucide-react";
import { toast } from "sonner";
import { ax, EmptyState } from "./sharedReview";

const fmtMoney = (v, code = "USD") => {
  if (v == null || v === "") return "—";
  const n = Number(v);
  if (Number.isNaN(n)) return "—";
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: code,
      currencyDisplay: "narrowSymbol",
      maximumFractionDigits: 0,
    }).format(n);
  } catch {
    return `${code} ${Math.round(n).toLocaleString("en-US")}`;
  }
};
const fmtMonths = (v) =>
  v == null || Number.isNaN(Number(v)) ? "—" : `${Number(v).toFixed(1)} mo`;
const fmtPct = (v) =>
  v == null || Number.isNaN(Number(v)) ? "—" : `${Number(v).toFixed(1)}%`;
const fmtWeeks = (v) =>
  v == null || Number.isNaN(Number(v)) ? "—" : `${Math.ceil(Number(v))} wks`;

const PRIORITY_PILL = {
  critical: "bg-[#FEF2F2] text-[#991B1B] border-[#FECACA]",
  high: "bg-[#FFF7ED] text-[#9A3412] border-[#FED7AA]",
  medium: "bg-[#FEFCE8] text-[#854D0E] border-[#FEF08A]",
  low: "bg-[#ECFDF5] text-[#065F46] border-[#A7F3D0]",
};

const VERDICT_BAND = {
  Recommended: "bg-[#ECFDF5] border-[#A7F3D0] text-[#065F46]",
  "Recommended with conditions": "bg-[#FFFBEB] border-[#FDE68A] text-[#92400E]",
  Hold: "bg-[#FEF2F2] border-[#FECACA] text-[#991B1B]",
  Pending: "bg-[#F1F5F9] border-[#E2E8F0] text-[#475569]",
};

export default function ExecutiveSummaryTab({ engagementId, isFinalized = false }) {
  const [bc, setBc] = useState(null);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const r = await ax.get(`/engagements/${engagementId}/business-case`);
      setBc(r.data || null);
    } catch {
      toast.error("Failed to load executive business case");
    } finally {
      setLoading(false);
    }
  }, [engagementId]);

  useEffect(() => {
    reload();
  }, [reload]);

  if (loading)
    return <div className="text-sm text-[#6B7280]">Loading executive business case…</div>;
  if (!bc) {
    return (
      <EmptyState
        icon={ScrollText}
        title="No business case yet"
        description="Capture findings, accept recommendations, and finalize implementation plans to assemble the boardroom view."
      />
    );
  }

  return <ExecutiveSummaryView bc={bc} isFinalized={isFinalized} />;
}

// Shared render used by BOTH the live tab and the version-snapshot viewer so
// the two never drift. Order: Header → Executive Summary → Recommended
// Improvement Portfolio → Implementation Roadmap → Top Value Drivers →
// Current Operational Challenges.
export function ExecutiveSummaryView({ bc, isFinalized = false }) {
  if (!bc) return null;
  return (
    <div className="space-y-6" data-testid="smartimprove-exec-summary-tab">
      <ExecutiveHeader header={bc.header} isFinalized={isFinalized} />
      <KpiStrip kpi={bc.kpi_strip} />
      <ExecutiveNarrative paragraphs={bc.executive_narrative} />
      <PortfolioSummary rows={bc.portfolio_summary} currency={bc.currency} />
      <ImplementationRoadmap roadmap={bc.implementation_roadmap} currency={bc.currency} />
      <ValueDrivers drivers={bc.value_drivers} currency={bc.currency} />
      <OperationalChallenges challenges={bc.operational_challenges} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// 1. Executive Header
// ---------------------------------------------------------------------------
function ExecutiveHeader({ header, isFinalized }) {
  if (!header) return null;
  const engagementLabel = (() => {
    const raw = header.engagement_type;
    if (!raw) return null;
    const map = {
      remote: "Remote",
      remote_review: "Remote Review",
      workshop: "Workshop",
      hybrid: "Hybrid",
      post_go_live: "Post Go Live",
      optimization: "Optimization",
    };
    const label = map[raw] || String(raw)
      .replace(/_/g, " ")
      .split(" ")
      .filter(Boolean)
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
      .join(" ");
    return `Engagement: ${label}`;
  })();
  const meta = [
    header.industry,
    header.business_model,
    engagementLabel,
  ].filter(Boolean);
  return (
    <article
      className="bg-gradient-to-br from-[#0B2A45] to-[#025979] text-white border border-[#01445E] rounded-sm p-6"
      data-testid="bc-executive-header"
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="text-[10px] font-semibold tracking-[0.22em] uppercase text-[#67E8F9] mb-1">
            Operational Improvement Business Case · Executive Decision Report
          </p>
          <h2
            className="text-2xl font-bold mb-1"
            style={{ fontFamily: "Chivo, sans-serif" }}
            data-testid="bc-company-name"
          >
            {header.company_name || "Engagement"}
          </h2>
          {meta.length ? (
            <p className="text-[11px] text-[#BAE6FD]">{meta.join(" · ")}</p>
          ) : null}
        </div>
        {isFinalized ? (
          <span className="text-[10px] uppercase tracking-wider bg-[#FEF3C7] text-[#92400E] px-2 py-1 rounded-sm border border-[#FCD34D]">
            Finalized — Read-only
          </span>
        ) : null}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-5">
        <HeaderTile
          icon={<Briefcase className="w-3.5 h-3.5" />}
          label="Strategic Objective"
          value={header.strategic_objective}
        />
        <HeaderTile
          icon={<Globe2 className="w-3.5 h-3.5" />}
          label="Geography"
          value={header.geography}
          subtext={(header.countries || []).join(", ")}
        />
        <HeaderTile
          icon={<Building2 className="w-3.5 h-3.5" />}
          label="Sites in Scope"
          value={header.site_count != null ? String(header.site_count) : "—"}
        />
        <HeaderTile
          icon={<Gauge className="w-3.5 h-3.5" />}
          label="Engagement Priority"
          value={header.priority ? header.priority.toUpperCase() : "—"}
        />
      </div>
    </article>
  );
}

function HeaderTile({ icon, label, value, subtext }) {
  return (
    <div className="bg-[rgba(255,255,255,0.06)] border border-[rgba(255,255,255,0.15)] rounded-sm px-3 py-2">
      <div className="flex items-center gap-1.5 text-[#67E8F9] text-[10px] uppercase tracking-wider mb-0.5">
        {icon}
        {label}
      </div>
      <p className="text-sm font-semibold text-white">{value || "—"}</p>
      {subtext ? <p className="text-[10px] text-[#BAE6FD] mt-0.5">{subtext}</p> : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// 2. KPI Strip
// ---------------------------------------------------------------------------
function KpiStrip({ kpi }) {
  if (!kpi) return null;
  const code = kpi.currency || "USD";
  return (
    <section
      className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3"
      data-testid="bc-kpi-strip"
    >
      <KpiTile
        icon={<DollarSign className="w-3.5 h-3.5" />}
        label="Total Investment"
        primary={fmtMoney(kpi.total_investment_usd, code)}
        secondary={
          kpi.total_recurring_usd
            ? `+${fmtMoney(kpi.total_recurring_usd, code)} / yr recurring`
            : "One-time (finalized plans)"
        }
        accent="#025979"
        testId="bc-kpi-investment"
      />
      <KpiTile
        icon={<TrendingUp className="w-3.5 h-3.5" />}
        label="Annual Savings"
        primary={fmtMoney(kpi.annual_savings_usd, code)}
        secondary={kpi.annual_savings_is_override ? "Consultant override" : "From ROI dashboard"}
        accent="#047857"
        testId="bc-kpi-savings"
      />
      <KpiTile
        icon={<CalendarRange className="w-3.5 h-3.5" />}
        label="Year-1 Benefit"
        primary={fmtMoney(kpi.year1_benefit_usd, code)}
        secondary="Ramp-adjusted realization"
        accent="#0E7490"
        testId="bc-kpi-year1"
      />
      <KpiTile
        icon={<Clock className="w-3.5 h-3.5" />}
        label="Payback"
        primary={fmtMonths(kpi.payback_months)}
        secondary="Time to break-even"
        accent="#C2410C"
        testId="bc-kpi-payback"
      />
      <KpiTile
        icon={<Award className="w-3.5 h-3.5" />}
        label="3-Year ROI"
        primary={fmtPct(kpi.three_year_roi_pct)}
        secondary={`Net ${fmtMoney(kpi.net_benefit_usd, code)}`}
        accent="#6E5BCE"
        testId="bc-kpi-roi"
      />
      <KpiTile
        icon={<CalendarRange className="w-3.5 h-3.5" />}
        label="Total Duration"
        primary={`${kpi.total_duration_weeks || 0} wks`}
        secondary={`${kpi.finalized_plan_count || 0} finalized · ${Math.round(kpi.total_effort_hours || 0).toLocaleString()} hrs`}
        accent="#1D4ED8"
        testId="bc-kpi-duration"
      />
    </section>
  );
}

function KpiTile({ icon, label, primary, secondary, accent, testId }) {
  return (
    <article
      className="bg-white border border-[#E5E7EB] rounded-sm p-3 relative overflow-hidden"
      data-testid={testId}
    >
      <span
        className="absolute left-0 top-0 bottom-0 w-1"
        style={{ background: accent }}
      />
      <div className="ml-1.5">
        <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-[#6B7280] mb-1">
          {icon}
          {label}
        </div>
        <p
          className="text-lg font-bold text-[#111827]"
          style={{ fontFamily: "Chivo, sans-serif" }}
        >
          {primary}
        </p>
        <p className="text-[10px] text-[#6B7280] mt-0.5">{secondary}</p>
      </div>
    </article>
  );
}

// ---------------------------------------------------------------------------
// 3. Current Operational Challenges — COMPACT executive summary (Area | Key
//    Challenge). Concise roll-up of the live findings/workstreams; no large
//    finding cards. Answers "what did the assessment find?".
// ---------------------------------------------------------------------------
function OperationalChallenges({ challenges }) {
  if (!challenges || !challenges.length) return null;
  const rows = challenges.slice(0, 6);
  return (
    <SectionShell
      icon={AlertTriangle}
      title="Current Operational Challenges"
      subtitle="The major operational challenge areas surfaced by the assessment."
      testId="bc-section-challenges"
    >
      <div className="bg-white border border-[#E5E7EB] rounded-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#F8FAFC] text-left text-[11px] uppercase tracking-wider text-[#6B7280]">
            <tr>
              <th className="px-3 py-2 font-semibold w-1/3">Area</th>
              <th className="px-3 py-2 font-semibold">Key Challenge</th>
              <th className="px-3 py-2 font-semibold text-right whitespace-nowrap">Findings</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((c) => (
              <tr
                key={c.key}
                className="border-t border-[#F1F5F9] align-top"
                data-testid={`bc-challenge-${c.key}`}
              >
                <td className="px-3 py-2">
                  <span className="font-medium text-[#111827]">{c.title}</span>
                </td>
                <td className="px-3 py-2 text-xs text-[#4B5563] leading-relaxed">
                  {c.key_challenge || "—"}
                </td>
                <td className="px-3 py-2 text-xs text-right text-[#111827] whitespace-nowrap">
                  {c.finding_count}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </SectionShell>
  );
}

// ---------------------------------------------------------------------------
// 4. Executive Narrative
// ---------------------------------------------------------------------------
function ExecutiveNarrative({ paragraphs }) {
  if (!paragraphs || !paragraphs.length) return null;
  return (
    <SectionShell
      icon={Lightbulb}
      title="Executive Summary"
      subtitle="A boardroom-level frame for the operational transformation."
      testId="bc-section-narrative"
    >
      <div className="space-y-3 text-sm text-[#374151] leading-relaxed bg-white border border-[#E5E7EB] rounded-sm p-5">
        {paragraphs.map((p, i) => (
          <p key={i} data-testid={`bc-narrative-p-${i}`}>
            {p}
          </p>
        ))}
      </div>
    </SectionShell>
  );
}

// ---------------------------------------------------------------------------
// 5. Recommendation Portfolio Summary — expandable executive cards.
//    Carries the business context (workstreams + findings addressed) that
//    previously lived in the Operational Challenges section. All content is
//    the existing persisted Recommendation / Workstream / Finding text.
// ---------------------------------------------------------------------------
function PortfolioSummary({ rows, currency }) {
  if (!rows || !rows.length) return null;
  const code = currency || "USD";
  return (
    <SectionShell
      icon={Target}
      title="Recommended Improvement Portfolio"
      subtitle={`${rows.length} recommendation${rows.length === 1 ? "" : "s"} with a finalized implementation plan (contributing to the business case).`}
      testId="bc-section-portfolio"
    >
      <div className="space-y-3">
        {rows.map((r) => (
          <RecCard key={r.code} r={r} code={code} />
        ))}
      </div>
    </SectionShell>
  );
}

function RecCard({ r, code }) {
  const [open, setOpen] = useState(false);
  const isStrategic = r.kind === "Strategic";
  const workstreams = r.workstreams || [];
  const findings = r.findings || [];
  const hasDetail = workstreams.length > 0 || findings.length > 0;
  return (
    <article
      className="bg-white border border-[#E5E7EB] rounded-sm overflow-hidden"
      data-testid={`bc-portfolio-row-${r.code}`}
    >
      <div className="p-4">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-xs font-bold text-[#025979] tracking-wide">{r.code}</span>
              <span
                className={`px-1.5 py-0.5 rounded-sm border text-[10px] uppercase tracking-wider ${
                  isStrategic
                    ? "bg-[#F0EDF9] border-[#DDD6FE] text-[#473F8B]"
                    : "bg-[#EFF6FF] border-[#BFDBFE] text-[#1D4ED8]"
                }`}
              >
                {isStrategic ? "Strategic" : "Primary"}
              </span>
              {r.complexity && r.complexity !== "—" ? (
                <span className="px-1.5 py-0.5 rounded-sm border border-[#E5E7EB] bg-[#F8FAFC] text-[10px] text-[#475569] uppercase tracking-wider">
                  {r.complexity}
                </span>
              ) : null}
            </div>
            <h4 className="text-sm font-semibold text-[#111827] leading-snug">{r.title}</h4>
            {r.summary ? (
              <p
                className="text-xs text-[#4B5563] mt-1.5 leading-relaxed line-clamp-2"
                title={r.summary}
              >
                {r.summary}
              </p>
            ) : null}
          </div>
          <div className="flex items-stretch gap-4 flex-shrink-0">
            <Metric
              label="Annual Benefit"
              value={r.annual_benefit_usd != null ? fmtMoney(r.annual_benefit_usd, code) : "—"}
              accentClass="text-[#047857]"
              testId={`bc-portfolio-benefit-${r.code}`}
            />
            <Metric
              label="Investment"
              value={fmtMoney(r.investment_usd, code)}
              accentClass="text-[#111827]"
              testId={`bc-portfolio-investment-${r.code}`}
            />
            <Metric
              label="Duration"
              value={fmtWeeks(r.duration_weeks)}
              accentClass="text-[#111827]"
              testId={`bc-portfolio-duration-${r.code}`}
            />
          </div>
        </div>
        {hasDetail ? (
          <button
            type="button"
            onClick={() => setOpen((o) => !o)}
            className="mt-3 inline-flex items-center gap-1 text-[11px] font-semibold text-[#025979] hover:text-[#013a4f] transition-colors"
            data-testid={`bc-portfolio-toggle-${r.code}`}
          >
            <ChevronRight
              className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-90" : ""}`}
            />
            {open ? "Hide detail" : `What this improves · ${workstreams.length} workstream${workstreams.length === 1 ? "" : "s"} · ${findings.length} finding${findings.length === 1 ? "" : "s"}`}
          </button>
        ) : null}
      </div>
      {open && hasDetail ? (
        <div
          className="border-t border-[#F1F5F9] bg-[#FBFCFD] px-4 py-3 grid grid-cols-1 md:grid-cols-2 gap-4"
          data-testid={`bc-portfolio-detail-${r.code}`}
        >
          {workstreams.length ? (
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-wider text-[#6B7280] mb-1.5">
                Workstreams
              </p>
              <ul className="space-y-2">
                {workstreams.map((w, i) => (
                  <li key={w.code || i} className="text-xs">
                    <span className="font-semibold text-[#111827]">
                      {w.title}
                      {w.code ? (
                        <span className="ml-1 text-[10px] font-normal text-[#94A3B8]">{w.code}</span>
                      ) : null}
                    </span>
                    {w.description ? (
                      <p
                        className="text-[#6B7280] mt-0.5 leading-relaxed line-clamp-2"
                        title={w.description}
                      >
                        {w.description}
                      </p>
                    ) : null}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
          {findings.length ? (
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-wider text-[#6B7280] mb-1.5">
                Addresses
              </p>
              <ul className="space-y-1">
                {findings.map((f, i) => (
                  <li key={f.code || i} className="flex items-start gap-1.5 text-xs text-[#374151]">
                    <ChevronRight className="w-3 h-3 text-[#7C3AED] mt-0.5 flex-shrink-0" />
                    <span>
                      {f.code ? (
                        <span className="text-[#94A3B8] mr-1">{f.code}</span>
                      ) : null}
                      {f.title || "—"}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </div>
      ) : null}
    </article>
  );
}

function Metric({ label, value, accentClass, testId }) {
  return (
    <div className="text-right min-w-[90px]">
      <p className="text-[10px] uppercase tracking-wider text-[#6B7280]">{label}</p>
      <p className={`text-sm font-bold ${accentClass}`} data-testid={testId}>
        {value}
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Implementation Roadmap & Resources — compact Delivery view built ONLY from
// finalized implementation plans (title, template/approach, duration, effort,
// investment, phase sequence) + an aggregated role Resource Summary. No rates,
// no cost model, no editable inputs, no full effort matrix.
// ---------------------------------------------------------------------------
function ImplementationRoadmap({ roadmap, currency }) {
  const plans = (roadmap && roadmap.plans) || [];
  const resources = (roadmap && roadmap.resources) || [];
  if (!plans.length) return null;
  const code = currency || "USD";
  return (
    <SectionShell
      icon={Layers}
      title="Implementation Roadmap & Resources"
      subtitle="Delivery plan and resourcing from the finalized implementation plans."
      testId="bc-section-roadmap"
    >
      <div className="space-y-3">
        {plans.map((p) => (
          <article
            key={p.code}
            className="bg-white border border-[#E5E7EB] rounded-sm p-4"
            data-testid={`bc-roadmap-plan-${p.code}`}
          >
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <span className="text-xs font-bold text-[#025979] tracking-wide">{p.code}</span>
                  <span
                    className={`px-1.5 py-0.5 rounded-sm border text-[10px] uppercase tracking-wider ${
                      p.is_strategic
                        ? "bg-[#F0EDF9] border-[#DDD6FE] text-[#473F8B]"
                        : "bg-[#EFF6FF] border-[#BFDBFE] text-[#1D4ED8]"
                    }`}
                  >
                    {p.is_strategic ? "Strategic" : "Primary"}
                  </span>
                  {p.template ? (
                    <span className="px-1.5 py-0.5 rounded-sm border border-[#E5E7EB] bg-[#F8FAFC] text-[10px] text-[#475569]">
                      {p.template}
                    </span>
                  ) : null}
                </div>
                <h4 className="text-sm font-semibold text-[#111827] leading-snug">{p.title}</h4>
              </div>
              <div className="flex items-stretch gap-4 flex-shrink-0">
                <Metric
                  label="Total Effort"
                  value={`${Math.round(p.effort_hours || 0).toLocaleString()} hrs`}
                  accentClass="text-[#111827]"
                  testId={`bc-roadmap-effort-${p.code}`}
                />
                <Metric
                  label="Investment"
                  value={fmtMoney(p.investment_usd, code)}
                  accentClass="text-[#111827]"
                  testId={`bc-roadmap-investment-${p.code}`}
                />
                <Metric
                  label="Duration"
                  value={fmtWeeks(p.duration_weeks)}
                  accentClass="text-[#111827]"
                  testId={`bc-roadmap-duration-${p.code}`}
                />
              </div>
            </div>
            {(p.phases || []).length ? (
              <div className="mt-3 pt-3 border-t border-[#F1F5F9]">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-[#6B7280] mb-1.5">
                  Phase Sequence
                </p>
                <div className="flex items-center flex-wrap gap-1.5" data-testid={`bc-roadmap-phases-${p.code}`}>
                  {p.phases.map((ph, i) => (
                    <span key={i} className="flex items-center gap-1.5">
                      <span className="px-2 py-0.5 rounded-sm bg-[#F8FAFC] border border-[#E5E7EB] text-[11px] text-[#374151]">
                        {ph}
                      </span>
                      {i < p.phases.length - 1 ? (
                        <ChevronRight className="w-3 h-3 text-[#9CA3AF]" />
                      ) : null}
                    </span>
                  ))}
                </div>
              </div>
            ) : null}
          </article>
        ))}
      </div>
    </SectionShell>
  );
}

// ---------------------------------------------------------------------------
// 6. Value Drivers
// ---------------------------------------------------------------------------
function ValueDrivers({ drivers, currency }) {
  if (!drivers || !drivers.length) return null;
  const code = currency || "USD";
  const maxShare = Math.max(...drivers.map((d) => d.share_pct || 0), 1);
  return (
    <SectionShell
      icon={Workflow}
      title="Top Value Drivers"
      subtitle="Where the operational savings concentrate — per recommendation."
      testId="bc-section-drivers"
    >
      <div className="bg-white border border-[#E5E7EB] rounded-sm p-4 space-y-3">
        {drivers.map((d) => (
          <div key={d.key} data-testid={`bc-driver-${d.key}`}>
            <div className="flex items-center justify-between text-xs mb-1">
              <span className="font-semibold text-[#111827]">{d.label}</span>
              <span className="text-[#6B7280]">
                {fmtMoney(d.amount_usd, code)} · {fmtPct(d.share_pct)}
              </span>
            </div>
            <div className="w-full h-2 bg-[#F1F5F9] rounded-sm overflow-hidden">
              <div
                className="h-full bg-[#025979]"
                style={{
                  width: `${Math.max((d.share_pct / maxShare) * 100, 2)}%`,
                }}
              />
            </div>
          </div>
        ))}
      </div>
    </SectionShell>
  );
}

// ---------------------------------------------------------------------------
// 7. Decision Statement
// ---------------------------------------------------------------------------
function DecisionStatement({ decision }) {
  if (!decision) return null;
  const band = VERDICT_BAND[decision.verdict] || VERDICT_BAND.Pending;
  return (
    <SectionShell
      icon={Award}
      title="Executive Decision Statement"
      subtitle="The boardroom recommendation."
      testId="bc-section-decision"
    >
      <article className={`border rounded-sm p-5 ${band}`} data-testid="bc-decision-card">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-[10px] font-bold uppercase tracking-[0.22em]">
            Verdict
          </span>
          <span
            className="text-sm font-bold px-2 py-0.5 rounded-sm bg-white/60 border border-current/30"
            data-testid="bc-decision-verdict"
          >
            {decision.verdict}
          </span>
        </div>
        <p
          className="text-base font-semibold mb-2"
          style={{ fontFamily: "Chivo, sans-serif" }}
          data-testid="bc-decision-headline"
        >
          {decision.headline}
        </p>
        <p className="text-sm leading-relaxed mb-3">{decision.body}</p>
        {decision.asks && decision.asks.length ? (
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-wider mb-1.5">
              Board is asked to:
            </p>
            <ol className="space-y-1 text-sm list-decimal list-inside">
              {decision.asks.map((a, i) => (
                <li key={i} data-testid={`bc-decision-ask-${i}`}>
                  {a}
                </li>
              ))}
            </ol>
          </div>
        ) : null}
      </article>
    </SectionShell>
  );
}

// ---------------------------------------------------------------------------
// 8. Appendix — Evidence Chain
// ---------------------------------------------------------------------------
function EvidenceChainAppendix({ chain }) {
  if (!chain || !chain.length) return null;
  return (
    <SectionShell
      icon={Layers}
      title="Appendix · Evidence Chain"
      subtitle="Issue → Observations → KPI → Insight → Recommendation traceability."
      testId="bc-section-appendix"
    >
      <ol className="space-y-4 bg-white border border-[#E5E7EB] rounded-sm p-5">
        {chain.map((node, i) => (
          <EvidenceNode key={i} node={node} index={i + 1} />
        ))}
      </ol>
    </SectionShell>
  );
}

function EvidenceNode({ node, index }) {
  const obs = node.supporting_observations || [];
  const kpis = node.linked_kpis || [];
  const recs = node.recommendations || [];
  return (
    <li
      className="relative pl-6 border-l-2 border-[#E5E7EB]"
      data-testid={`evidence-chain-node-${index}`}
    >
      <span className="absolute -left-[9px] top-0 flex items-center justify-center w-4 h-4 rounded-full bg-[#025979] text-white text-[10px] font-bold">
        {index}
      </span>
      <div className="space-y-2">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[10px] font-semibold uppercase tracking-wider text-[#6B7280]">
            Insight
          </span>
          {node.severity ? (
            <span className="px-1.5 py-0.5 text-[10px] rounded-sm border border-[#FED7AA] bg-[#FFF7ED] text-[#9A3412]">
              {node.severity}
            </span>
          ) : null}
          {node.operational_area ? (
            <span className="px-1.5 py-0.5 text-[10px] rounded-sm border border-[#E5E7EB] bg-[#F8FAFC] text-[#374151]">
              {node.operational_area}
            </span>
          ) : null}
        </div>
        <h4 className="text-sm font-semibold text-[#111827]">{node.insight_title}</h4>

        <ChainStep label="Supporting observations" count={obs.length}>
          {obs.length === 0 ? (
            <p className="text-xs text-[#6B7280] italic">No linked observations.</p>
          ) : (
            <ul className="text-xs text-[#374151] space-y-0.5">
              {obs.map((o, i) => (
                <li key={o.id || i}>↳ {o.note}</li>
              ))}
            </ul>
          )}
        </ChainStep>

        <ChainStep label="Affected KPIs" count={kpis.length}>
          {kpis.length === 0 ? (
            <p className="text-xs text-[#6B7280] italic">No KPI linkage.</p>
          ) : (
            <div className="flex flex-wrap gap-1">
              {kpis.map((k, i) => (
                <span
                  key={i}
                  className="px-1.5 py-0.5 text-[10px] rounded-sm border border-[#E5E7EB] bg-[#F8FAFC] text-[#374151]"
                >
                  {k}
                </span>
              ))}
            </div>
          )}
        </ChainStep>

        <ChainStep label="Recommendations" count={recs.length}>
          {recs.length === 0 ? (
            <p className="text-xs text-[#6B7280] italic">No downstream recommendation.</p>
          ) : (
            <ul className="space-y-1 text-xs text-[#374151]">
              {recs.map((r, i) => (
                <li key={i}>↳ {r.title}</li>
              ))}
            </ul>
          )}
        </ChainStep>
      </div>
    </li>
  );
}

function ChainStep({ label, count, children }) {
  return (
    <section className="bg-[#F8FAFC] border border-[#E5E7EB] rounded-sm px-3 py-2">
      <p className="text-[10px] font-semibold uppercase tracking-wider text-[#6B7280] mb-1">
        {label}
        {count != null ? <span className="ml-1">({count})</span> : null}
      </p>
      {children}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Shared section shell
// ---------------------------------------------------------------------------
function SectionShell({ icon: Icon, title, subtitle, children, testId }) {
  return (
    <section data-testid={testId}>
      <header className="flex items-center gap-2 mb-2">
        {Icon ? <Icon className="w-4 h-4 text-[#025979]" /> : null}
        <h3
          className="text-base font-bold text-[#111827]"
          style={{ fontFamily: "Chivo, sans-serif" }}
        >
          {title}
        </h3>
      </header>
      {subtitle ? (
        <p className="text-[11px] text-[#6B7280] mb-3 -mt-1">{subtitle}</p>
      ) : null}
      {children}
    </section>
  );
}
